/**
 * AutoGrowTextarea — textarea que crece en altura con el contenido (rediseño
 * Home v2, dirección «Mesa»: «el recuadro crece con lo que escribas»).
 *
 * Recalcula la altura en cada cambio de `value`: la lleva a `auto`, mide el
 * `scrollHeight` y la fija a ese alto hasta un tope de `maxHeight` px; a partir
 * de ahí, scroll interno. El tope se aplica en JS (belt) y también vive como
 * `max-height` en CSS (suspenders). El comportamiento de Enter/Shift+Enter lo
 * decide el `onKeyDown` que le pase quien lo usa (Enter envía en Home y chat).
 */
import { forwardRef, useCallback, useLayoutEffect, useRef } from 'react'

const AutoGrowTextarea = forwardRef(function AutoGrowTextarea(
  { value, maxHeight = 240, className, ...rest },
  forwardedRef,
) {
  const innerRef = useRef(null)

  // Un solo nodo, compartido con la ref que nos pasen (callback u objeto).
  const setRefs = useCallback((node) => {
    innerRef.current = node
    if (typeof forwardedRef === 'function') forwardedRef(node)
    else if (forwardedRef) forwardedRef.current = node
  }, [forwardedRef])

  const resize = useCallback(() => {
    const el = innerRef.current
    if (!el) return
    // Reset a "auto" para medir el contenido real (y para que un campo vacío
    // ocupe una sola fila, rows=1).
    el.style.height = 'auto'
    // Un textarea vacío no debe crecer: en Chrome, si el `placeholder` se parte
    // en varias líneas (contenedor muy estrecho), `scrollHeight` cuenta el
    // placeholder e inflaría el alto. Con el valor vacío nos quedamos en 1 fila.
    if (el.value === '') {
      el.style.overflowY = 'hidden'
      return
    }
    // box-sizing:border-box -> la altura CSS incluye el borde, pero scrollHeight
    // no; súmalo para no recortar 1-2px y provocar un scroll fantasma.
    const cs = typeof window !== 'undefined' ? window.getComputedStyle(el) : null
    const borderY = cs
      ? (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0)
      : 0
    const full = el.scrollHeight + borderY
    const capped = Math.min(full, maxHeight)
    el.style.height = `${capped}px`
    el.style.overflowY = full > maxHeight ? 'auto' : 'hidden'
  }, [maxHeight])

  // Recalcular al montar y en cada cambio de value (incluye el reset al enviar).
  useLayoutEffect(() => { resize() }, [value, resize])

  return (
    <textarea
      ref={setRefs}
      className={className}
      value={value}
      rows={1}
      {...rest}
    />
  )
})

export default AutoGrowTextarea
